import { useCallback, useEffect } from 'react';

import qs from 'query-string';
import { useLocation } from 'react-router';

import type { AnalyticsEventKey, AnalyticsSchema } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { segmentTrack } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';

/**
 * You can pass any required payload either when you call the hook OR when you dispatch the thunk.
 * This is why the payload is typed as optional in both the callback AND the hook parameters.
 *
 * Why does this AND `useSegmentTrack` exist?:
 *
 * 1. `useSegmentTrack` only supports a non-treeshakeable interface. That is to say you're required to import the entire
 * UNIFIED object and use it in one of the hook's arguments. Since Next.js page files are automatically code split,
 * this would mean that some pages would include unused code, needlessly worsening performance.
 *
 * 2. `useSegmentTrack` includes business logic which always triggers API requests. This hook was made with only
 * abstracted usage in mind.
 *
 * 3. We want to discuss deprecation of the others as a group before actually deprecating them.
 *
 * If you're passing data to the payload of this hook, please be cognizant of how the relevant data resolves.
 * You can't do conditional hooks, but you CAN conditionally render components with a hook. So, if you're not
 * certain that data passed into `payload` will be available by the time this hook is called, move the hook into
 * a renderless component that is conditionally rendered after all of your data has resolved.
 *
 * @example
 *
 * const RenderlessComponentWithAnalytics = props => {
 *   useSegmentEffect('yolo', { data: props.data }, { shouldTrackOnMount: true });
 *   return null;
 * };
 *
 * const SomeOtherComponent = props => {
 *   const someScaryData = getIt();
 *
 *   return (
 *     <>
 *       {someScaryData && <RenderlessComponentWithAnalytics data={someScaryData} />
 *       <div>Yeehaw</div>
 *     </>
 *   );
 * };
 */
/**
 * Custom hook for tracking Segment events with automatic gclid inclusion
 *
 * @template TEventName - The type of the event name (must be a key of AnalyticsSchema)
 * @template TEventProperties - The type of the event properties
 * @template TPayload - The type of the payload object
 *
 * @param eventName - The name of the event to track
 * @param options - Configuration options for the hook
 * @param options.payload - The payload to send with the event (required if shouldTrackOnMount is true)
 * @param options.shouldTrackOnMount - Whether to track the event when the component mounts
 *
 * @returns A function to dispatch the Segment event
 */
export const useSegmentEvent = <
  TEventName extends AnalyticsEventKey,
  TEventProperties extends AnalyticsSchema[TEventName]['properties'],
  TPayload extends {
    [key in TEventProperties[number]]: null | string | boolean | number | string[] | number[] | (string | number)[];
  }
>(
  eventName: TEventName,
  {
    payload,
    shouldTrackOnMount,
  }: keyof TPayload extends never
    ? { payload?: never; shouldTrackOnMount?: boolean }
    : { payload: Required<TPayload>; shouldTrackOnMount: true } | { payload?: never; shouldTrackOnMount?: false }
) => {
  const dispatch = useDispatch();
  const location = useLocation();

  /**
   * Function to get URL parameters
   * @param param - The name of the URL parameter to retrieve
   * @returns The value of the URL parameter or null if not found
   */
  const getUrlParam = useCallback(
    (param: string): string | null => {
      const parsedQuery = qs.parse(location?.search);
      const value = parsedQuery[param];
      return typeof value === 'string' ? value : null;
    },
    [location]
  );

  /**
   * Callback function to dispatch Segment events with enhanced payload
   */
  const dispatchSegmentEvent = useCallback(
    (...rest: keyof TPayload extends never ? [payload?: never] : [payload: Required<TPayload>]) => {
      // Get the gclid from URL parameters
      const gclid = getUrlParam('gclid');

      // Enhance the payload with gclid
      const enhancedPayload = {
        ...rest[0],
        gclid: gclid || undefined,
      };

      // Dispatch the Segment track action with the enhanced payload
      dispatch(segmentTrack(UNIFIED[eventName] as any, enhancedPayload));
    },
    [dispatch, eventName]
  );

  /**
   * Effect to track the event on component mount if shouldTrackOnMount is true
   */
  useEffect(() => {
    if (shouldTrackOnMount) {
      // Cast payload to any to satisfy TypeScript
      // This is safe because if shouldTrackOnMount is true, payload must be provided
      dispatchSegmentEvent(payload as any);
    }
  }, []); // Empty dependency array ensures this runs only on mount

  return dispatchSegmentEvent;
};
